import { useState, useEffect, useCallback } from "react";
import api from '../api/axios';
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUser, FaCheck, FaTimes, FaTrash, FaArrowLeft, FaRedo } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import '../styles/common.css';
import './ModifierBerger.css';

export default function ModifierBerger() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    telephone: "",
    cni: "",
    adresse: "",
    date_embauche: "",
    actif: true,
  });
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    prenom: "",
    cni: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    prenom: false,
    cni: false,
  });

  // Fonction réutilisable pour charger le berger
  const fetchBerger = useCallback(async () => {
    try {
      const response = await api.get(`/api/bergers/${id}/`);
      setFormData(response.data);
      setInitialData(JSON.parse(JSON.stringify(response.data)));
      setFetchError(null);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      
      let errorMessage = "Impossible de charger le berger.";
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Le berger demandé n'existe pas.";
        } else if (error.response.status === 403) {
          errorMessage = "Vous n'avez pas la permission d'accéder à ce berger.";
        } else if (error.response.status >= 500) {
          errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
        } else {
          errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
      }
      
      setFetchError(errorMessage);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBerger();
  }, [fetchBerger]);

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || value.trim() === "") {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    // Validation en temps réel si le champ a été touché
    if ((name === "prenom" || name === "cni") && touchedFields[name]) {
      const error = validateRequiredField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Valider les champs obligatoires
    if (name === "prenom" || name === "cni") {
      const error = validateRequiredField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const hasChanges = () => {
    if (!initialData) return false;
    
    // Normaliser les valeurs pour la comparaison
    const normalize = (val) => {
      if (val === null || val === undefined) return "";
      if (typeof val === 'boolean') return val;
      return String(val).trim();
    };
    
    return (
      normalize(formData.prenom) !== normalize(initialData.prenom) ||
      normalize(formData.nom) !== normalize(initialData.nom) ||
      normalize(formData.telephone) !== normalize(initialData.telephone) ||
      normalize(formData.cni) !== normalize(initialData.cni) ||
      normalize(formData.adresse) !== normalize(initialData.adresse) ||
      normalize(formData.date_embauche) !== normalize(initialData.date_embauche) ||
      formData.actif !== initialData.actif
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasChanges()) {
      return;
    }
    
    // Marquer tous les champs obligatoires comme touchés
    setTouchedFields(prev => ({ ...prev, prenom: true, cni: true }));
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      prenom: validateRequiredField("prenom", formData.prenom),
      cni: validateRequiredField("cni", formData.cni),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = newFieldErrors.prenom || newFieldErrors.cni;
    
    if (hasErrors) {
      return;
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/api/bergers/${id}/`, formData);
      setShowConfirmModal(false);
      toast.success("Berger modifié avec succès !");
      setTimeout(() => {
        navigate("/enregistrer-berger");
      }, 500);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la mise à jour du berger.";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const handleRetry = () => {
    setFetchError(null);
    setLoading(true);
    fetchBerger();
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
      try {
        await api.delete(`/api/bergers/${id}/`);
      setShowDeleteModal(false);
        toast.success("Berger supprimé avec succès !");
        setTimeout(() => {
          navigate("/enregistrer-berger");
        }, 500);
      } catch (error) {
        console.error("Erreur lors de la suppression :", error);
        const errorMessage = error.response?.data?.detail || 
                            error.response?.data?.message ||
                            Object.values(error.response?.data || {}).flat().join(", ") ||
                            "Une erreur s'est produite lors de la suppression.";
        toast.error(errorMessage);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="modifier-berger-page">
        <div className="loading-container">
          <p className="loading-text">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="modifier-berger-page">
        <div className="title-badge">
          <FaUser className="page-title-icon" aria-hidden="true" />
          <span>{t.error}</span>
        </div>
        <div className="error-container">
          <p className="error-message">{fetchError}</p>
          <div className="actions">
            <button
              type="button"
              className="save-but"
              onClick={handleRetry}
            >
              <FaRedo />
              <span>{t.retry}</span>
            </button>
            <button
              type="button"
              className="cancel-but"
              onClick={() => navigate("/enregistrer-berger")}
            >
              <FaArrowLeft />
              <span>{t.back}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-berger-page">
      <div className="title-badge">
        <FaUser className="page-title-icon" aria-hidden="true" />
        <span>MODIFIER LE BERGER {formData.prenom || id}</span>
      </div>

      <form onSubmit={handleSubmit} className="berger-form">
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="prenom">
              {t.firstName} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="prenom"
              name="prenom"
              value={formData.prenom}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Ex: Baba"
              className={fieldErrors.prenom && touchedFields.prenom ? "input-error" : ""}
              required
            />
            {fieldErrors.prenom && touchedFields.prenom && (
              <span className="field-error-message">{fieldErrors.prenom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="nom">{t.lastName}</label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom || ""}
              onChange={handleChange}
              placeholder="Ex: Diarra"
            />
          </div>

          <div className="form">
            <label htmlFor="telephone">{t.phone}</label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone || ""}
              onChange={handleChange}
              placeholder="Ex: +223 76 12 34 56"
            />
          </div>

          <div className="form">
            <label htmlFor="cni">
              {t.cni} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="cni"
              name="cni"
              value={formData.cni || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Ex: A123456"
              className={fieldErrors.cni && touchedFields.cni ? "input-error" : ""}
              required
            />
            {fieldErrors.cni && touchedFields.cni && (
              <span className="field-error-message">{fieldErrors.cni}</span>
            )}
          </div>

          <div className="form full-width">
            <label htmlFor="adresse">{t.address}</label>
            <textarea
              id="adresse"
              name="adresse"
              value={formData.adresse || ""}
              onChange={handleChange}
              placeholder="Ex: Bamako"
              rows="3"
            />
          </div>

          <div className="form">
            <label htmlFor="date_embauche">{t.hireDate}</label>
            <input
              type="date"
              id="date_embauche"
              name="date_embauche"
              value={formData.date_embauche || ""}
              onChange={handleChange}
            />
          </div>

          <div className="form">
            <label htmlFor="actif">
              <input
                type="checkbox"
                id="actif"
                name="actif"
                checked={formData.actif}
                onChange={handleChange}
              />
              <span style={{ marginLeft: '8px' }}>{t.active}</span>
            </label>
          </div>
        </div>

        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!hasChanges()}
          >
            <FaCheck />
            <span>Enregistrer</span>
          </button>
          <button
            type="button"
            className="delete-but"
            onClick={handleDeleteClick}
          >
            <FaTrash />
            <span>Supprimer</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/enregistrer-berger")}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer la modification</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les modifications avant de confirmer :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Prénom :</span>
                  <span className="summary-value">{formData.prenom || "Non renseigné"}</span>
                </div>
                {formData.nom && (
                  <div className="summary-row">
                    <span className="summary-label">Nom :</span>
                    <span className="summary-value">{formData.nom}</span>
                  </div>
                )}
                {formData.telephone && (
                  <div className="summary-row">
                    <span className="summary-label">Téléphone :</span>
                    <span className="summary-value">{formData.telephone}</span>
                  </div>
                )}
                {formData.cni && (
                  <div className="summary-row">
                    <span className="summary-label">CNI :</span>
                    <span className="summary-value">{formData.cni}</span>
                  </div>
                )}
                {formData.adresse && (
                  <div className="summary-row">
                    <span className="summary-label">Adresse :</span>
                    <span className="summary-value">{formData.adresse}</span>
                  </div>
                )}
                {formData.date_embauche && (
                  <div className="summary-row">
                    <span className="summary-label">Date d'embauche :</span>
                    <span className="summary-value">{formData.date_embauche}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">Actif :</span>
                  <span className="summary-value">{formData.actif ? "Oui" : "Non"}</span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={submitting}
              >
                <FaTimes />
                <span>Annuler</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={submitting}
              >
                <FaCheck />
                <span>{submitting ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        itemName={`${formData.prenom || ""} ${formData.nom || ""}`.trim() || "ce berger"}
        itemType="berger"
        consequences={[]}
        requireConfirmation={false}
        isLoading={isDeleting}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

